import { useEffect, useMemo, useState } from "react";
import ExposureCard, {
  type Exposure,
  type ExposureStatus,
  type ExposureType,
} from "../components/ExposureCard";
import PaywallModal from "../components/PaywallModal";
import {
  fetchMe,
  listExposures,
  listSubjects,
  requestRemovals,
  scan as apiScan,
  type Exposure as ApiExposure,
  type MeResponse,
} from "../lib/api";

const NAVY = "#0b1f4a";
const ACCENT = "#b68235";
const ACCENT_100 = "#fff3e4";
const ACCENT_800 = "#5a3b0a";

const INITIAL: Exposure[] = [];

function mapApi(list: ApiExposure[]): Exposure[] {
  return list.map((e) => {
    const sevRaw = (e.severity || "").toLowerCase();
    const severity = (
      sevRaw === "high" || sevRaw === "hög"
        ? "Hög"
        : sevRaw === "low" || sevRaw === "låg"
          ? "Låg"
          : sevRaw === "medel" || sevRaw === "medium"
            ? "Medel"
            : e.severity === "Hög" || e.severity === "Låg" || e.severity === "Medel"
              ? e.severity
              : "Medel"
    ) as Exposure["severity"];
    return {
      id: e.id,
      apiId: e.id,
      source: e.sourceName || e.sourceId,
      type: (e.sourceType === "serp" ||
      e.sourceId === "serp" ||
      e.sourceId === "google" ||
      e.sourceId === "bing"
        ? "sok"
        : "upp") as ExposureType,
      snippet:
        e.snippet ||
        [e.mentionedOnPage ? "Namn träffades på sidan" : "Källa att granska / dölja", e.strategy, e.site]
          .filter(Boolean)
          .join(" · "),
      url: e.url,
      severity,
      status: (e.status as ExposureStatus) || "DISCOVERED",
    };
  });
}

type FilterKey = "alla" | ExposureType;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "alla", label: "Alla" },
  { key: "upp", label: "Upplysningar" },
  { key: "sok", label: "Sökmotorer" },
];

/**
 * Hitta — scan, filter chips, exposures (MrKoll / Ratsit / Google / Bing …).
 * Mock only. Route suggestion: /find or /hitta
 */
export default function Find() {
  const [exposures, setExposures] = useState<Exposure[]>(INITIAL);
  const [filter, setFilter] = useState<FilterKey>("alla");
  const [scanning, setScanning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [city, setCity] = useState("");
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [lastScanAt, setLastScanAt] = useState<string | null>(null);
  const [busyRemove, setBusyRemove] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallCount, setPaywallCount] = useState(1);
  const [me, setMe] = useState<MeResponse | null>(null);

  // Prefill name + preload saved/demo exposures so Hitta shows the full list
  // immediately (App Review /demo must not look empty until "Kör scan").
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetchMe();
        if (!alive) return;
        setMe(r.data);
        if (r.data?.name) setFullName(r.data.name);
      } catch {
        /* utloggad — fältet får stå tomt */
      }
      try {
        const [subj, exp] = await Promise.all([listSubjects(), listExposures()]);
        if (!alive) return;
        const sid = subj.data?.[0]?.id ?? exp.data?.[0]?.subjectId ?? null;
        if (sid) setSubjectId(sid);
        if (exp.data?.length) {
          setExposures(mapApi(exp.data));
          setLastScanAt("Demoprofil (sparad)");
        }
      } catch {
        /* nya konton har ingen sparad scan ännu */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  };

  const visible = useMemo(
    () => exposures.filter((x) => filter === "alla" || x.type === filter),
    [exposures, filter],
  );

  const hasApprovable = visible.some((x) => x.status === "DISCOVERED");

  const runScan = async () => {
    if (!fullName.trim()) {
      showToast("Ange namn för scan");
      return;
    }
    setScanning(true);
    try {
      const res = await apiScan({ fullName: fullName.trim(), city: city.trim() || undefined, country: "SE" });
      setSubjectId(res.data.subject.id);
      setExposures(mapApi(res.data.exposures));
      setLastScanAt(new Date().toLocaleString("sv-SE"));
      showToast(
        `Scan klar — ${res.data.scan.counts.total} källor (${res.data.scan.counts.mentioned ?? 0} namnträff)`,
      );
    } catch (e) {
      showToast(`Scan misslyckades: ${e instanceof Error ? e.message : "fel"}`);
    } finally {
      setScanning(false);
    }
  };

  const openPaywall = (count: number) => {
    setPaywallCount(count);
    setPaywallOpen(true);
  };

  const queueRemovals = async (ids: (string | number)[]) => {
    if (!subjectId) {
      showToast("Kör en scan först");
      return;
    }
    // Gratisnivå: visa betalning direkt — inte ett rött felmeddelande.
    if (me && !me.entitlement?.canRemove) {
      openPaywall(ids.length);
      return;
    }
    const exposureIds = ids.map(String);
    setBusyRemove(true);
    try {
      const res = await requestRemovals(subjectId, exposureIds);
      const byExp = new Map(
        (res.data.tickets || []).map((t) => [String(t.exposureId || ""), t]),
      );
      setExposures((list) =>
        list.map((e) => {
          if (!exposureIds.includes(String(e.id))) return e;
          const t = byExp.get(String(e.id));
          return {
            ...e,
            status: (t?.status as ExposureStatus) || "QUEUED",
          };
        }),
      );
      showToast(`${res.data.tickets.length} borttagningsärenden skapade`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      if (msg === "payment_required") {
        openPaywall(ids.length);
      } else {
        showToast(`Borttagning misslyckades: ${msg || "fel"}`);
      }
    } finally {
      setBusyRemove(false);
    }
  };

  const onApprove = (id: number | string) => {
    void queueRemovals([id]);
  };

  const onNotMe = (id: number | string) => {
    setExposures((list) => list.filter((e) => e.id !== id));
    showToast('Markerad som "Inte jag"');
  };

  const approveAll = () => {
    const ids = visible.filter((x) => x.status === "DISCOVERED").map((x) => x.id);
    void queueRemovals(ids);
  };

  return (
    <div data-page="find" style={{ fontFamily: '"Lora", Georgia, serif', color: "#201f1d" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <h1
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 400,
            fontSize: 34,
            margin: 0,
            color: NAVY,
            flex: 1,
          }}
        >
          Hitta
        </h1>
        <span style={{ fontSize: 12.5, color: "#9b9797", fontFeatureSettings: '"tnum" 1' }}>
          {lastScanAt ? `Senaste scan: ${lastScanAt}` : "Ingen scan ännu — API live"}
        </span>
        <button
          type="button"
          onClick={() => void runScan()}
          disabled={scanning || busyRemove}
          style={{
            padding: "11px 20px",
            border: `1px solid ${NAVY}`,
            borderRadius: 4,
            background: "transparent",
            fontSize: 14.5,
            color: NAVY,
            cursor: scanning ? "wait" : "pointer",
            opacity: scanning ? 0.7 : 1,
          }}
        >
          {scanning ? "Scannar…" : "Kör scan"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", alignItems: "end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
          Fullständigt namn
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={{ padding: "10px 12px", border: "1px solid #d7d3d3", borderRadius: 4, minWidth: 220 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
          Ort (valfritt)
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ padding: "10px 12px", border: "1px solid #d7d3d3", borderRadius: 4, minWidth: 140 }}
          />
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
        {FILTERS.map((c) => {
          const on = filter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilter(c.key)}
              style={{
                padding: "8px 16px",
                borderRadius: 99,
                fontSize: 13,
                cursor: "pointer",
                background: on ? ACCENT_100 : "transparent",
                border: on ? `1px solid ${ACCENT}` : "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                color: on ? ACCENT_800 : "#7d7979",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {scanning && (
        <div
          style={{
            background: "#fff",
            border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
            borderRadius: 7,
            padding: 26,
            marginTop: 20,
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div
            style={{
              width: 26,
              height: 26,
              border: "3px solid #ffe3bf",
              borderTopColor: ACCENT,
              borderRadius: "50%",
              animation: "gdpr-spin 0.9s linear infinite",
            }}
          />
          <span style={{ fontSize: 14.5, color: "#7d7979" }}>
            Söker Google, Bing och upplysningar…
          </span>
          <style>{`@keyframes gdpr-spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 20 }}>
        {visible.map((x) => (
          <ExposureCard
            key={x.id}
            exposure={x}
            onApprove={onApprove}
            onNotMe={onNotMe}
            onOpen={() => {
              if (x.url) window.open(x.url, "_blank", "noopener,noreferrer");
              else showToast(`Källa: ${x.source}`);
            }}
          />
        ))}
        {visible.length === 0 && (
          <div style={{ fontSize: 14.5, color: "#9b9797", padding: "24px 0" }}>
            Inga exponeringar matchar filtret.
          </div>
        )}
      </div>

      {hasApprovable && (
        <button
          type="button"
          onClick={approveAll}
          style={{
            marginTop: 18,
            padding: "12px 24px",
            border: `1px solid ${ACCENT}`,
            borderRadius: 4,
            background: "transparent",
            fontSize: 14.5,
            color: "#7d5411",
            cursor: "pointer",
          }}
        >
          Godkänn alla synliga
        </button>
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            background: NAVY,
            color: "#fff",
            borderRadius: 4,
            padding: "12px 22px",
            fontSize: 14,
            boxShadow: "0 12px 32px color-mix(in srgb, #2d2b2b 22%, transparent)",
            zIndex: 80,
          }}
        >
          {toast}
        </div>
      )}

      <PaywallModal
        open={paywallOpen}
        pendingCount={paywallCount}
        onClose={() => setPaywallOpen(false)}
      />
    </div>
  );
}
